// LegacyScreen — the finale at age 18. Everything the player built, in one proud
// summary: wealth, passive income, freedom, the dreams realised, the circle they
// kept, the quadrant they reached.

import { motion } from 'framer-motion';
import { Card, Eyebrow, Button, money } from '../ui/kit.js';
import { useLife, useLifeComputed } from './store.js';

const QUADRANT_NAME: Record<string, string> = { E: 'Employee', S: 'Self-employed', B: 'Business owner', I: 'Investor' };

export function LegacyScreen({ onClose }: { onClose: () => void }) {
  const s = useLife();
  const c = useLifeComputed();
  const achieved = s.visionBoard.filter((v) => v.achieved);

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 84, overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: 'clamp(16px, 4vw, 30px)', paddingBottom: 80 }}>
        <motion.div initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 60 }}>🏆</div>
          <Eyebrow color="var(--brand)">Your legacy at 18</Eyebrow>
          <h1 style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 900, color: 'var(--text)' }}>Look what you built.</h1>
          {c.freedomMode && (
            <div style={{ marginTop: 8, display: 'inline-block', padding: '6px 14px', borderRadius: 999, background: 'var(--up-soft)', color: 'var(--up)', fontWeight: 800, fontSize: 13 }}>
              🔓 You reached Freedom Mode
            </div>
          )}
        </motion.div>

        {/* headline numbers */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Big label="Total wealth" value={money(c.totalWealth)} />
            <Big label="Passive income" value={`${money(c.passiveIncome)}/mo`} accent="var(--up)" />
            <Big label="Freedom Meter" value={`${c.freedomPct}%`} accent={c.freedomMode ? 'var(--up)' : 'var(--brand)'} />
            <Big label="Quadrant reached" value={`${s.quadrant} · ${QUADRANT_NAME[s.quadrant]}`} />
          </div>
        </Card>

        {/* dreams realised */}
        <Card style={{ marginBottom: 14 }}>
          <Eyebrow color="var(--up)">Dreams realised · {achieved.length}/{s.visionBoard.length}</Eyebrow>
          {achieved.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {achieved.map((v) => (
                <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'var(--up-soft)', border: '1px solid var(--up)', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                  <span>{v.icon}</span> {v.title}
                </span>
              ))}
            </div>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-3)' }}>Every dream is a fresh start — there’s always a next chapter.</p>
          )}
        </Card>

        {/* assets + circle */}
        <Card style={{ marginBottom: 14 }}>
          <Eyebrow>What pays you · {s.assets.length} assets</Eyebrow>
          <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
            {s.assets.length ? s.assets.map((a) => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                <span style={{ color: 'var(--text-2)' }}>📈 {a.name}</span>
                <span className="bk-tnum" style={{ color: 'var(--up)', fontWeight: 800 }}>+{money(a.monthlyIncome)}/mo</span>
              </div>
            )) : <span style={{ fontSize: 13, color: 'var(--text-3)' }}>No assets yet.</span>}
          </div>
          {s.mastermind.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-2)' }}>
              Your circle: {s.mastermind.map((m) => `${m.icon} ${m.name}`).join(' · ')}
            </div>
          )}
        </Card>

        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 13.5, color: 'var(--text-2)', maxWidth: 420, margin: '0 auto 14px', lineHeight: 1.55 }}>
            You started with a stand and a dream. You learned to make money work for you. That’s a foundation for life.
          </p>
          <Button tone="primary" size="lg" onClick={onClose}>Keep playing</Button>
        </div>
      </div>
    </div>
  );
}

function Big({ label, value, accent = 'var(--text)' }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700 }}>{label}</div>
      <div className="bk-tnum" style={{ fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    </div>
  );
}
